// ============================================================================
//  MembershipManagement — the free NHIS renewal register
// ============================================================================
//
//  The facility renews NHIS membership free of charge. A number is taken at the
//  desk; the renewal is done later, on a phone, by whoever is working the list.
//
//  ── TWO BUCKETS AND A BAR ──────────────────────────────────────────────────
//
//  PENDING and RENEWED, in that order, and a progress bar between them. The bar
//  is not decoration: a promotion is measured by how much of it is done, and
//  "1,240 of 10,000" is the question everyone running it actually asks. Two
//  bare counts make you do that arithmetic yourself, every time you look.
//
//  ── ONE FIELD, EIGHT DIGITS ────────────────────────────────────────────────
//
//  The form asks for the NHIS number and nothing else. Non-digits are stripped
//  as they are typed, because people read numbers aloud in groups. The server
//  applies the same rule — see checkNumber in maintenanceRoutes — because a
//  seven-digit number here is worse than a refused one: it gets phoned, fails,
//  and counts against the campaign as a failure that was never a membership.
//
//  ── THE COLUMNS ────────────────────────────────────────────────────────────
//
//  A tick box, the number, its status, WHEN IT CAME IN, who recorded it, and
//  what to do about it. The folder number is still held and still searchable;
//  it is not a column, because it never helped anyone decide whether to press
//  the button. "Recorded by" hides below xl — on a narrow screen the number
//  needs that width more.
//
//  The Taken column carries the date AND the clock time. It used to say only
//  "Today", which tells you nothing once a desk has taken forty numbers since
//  morning: you cannot separate the 7am walk-ins from the 4pm ones, cannot see
//  what a shift collected, and cannot answer a member asking when they handed
//  their card over.
//
//  ── BUILT FOR TEN THOUSAND, NOT TWO HUNDRED ────────────────────────────────
//
//  Paging, sorting and the export all happen on the SERVER. The screen used to
//  ask for 200 rows and draw them with no page control, so past row 200 the
//  register simply was not there and nothing said so. Sorting in the browser
//  has the same flaw in a worse form: it reorders the page you can see and
//  looks authoritative while being wrong about the other 9,800.
//
//  ── AND FOR SOMEONE WORKING A LIST ─────────────────────────────────────────
//
//  Tick several numbers and renew them in one press. Each is still checked with
//  NHIA individually and recorded on its own answer — the batch saves the
//  twenty interruptions, not the twenty questions. Sequential rather than
//  parallel: a burst of twenty requests to somebody else's service is how a
//  rate limit turns into twenty failures.
//
//  ── BUILT FOR A PHONE ──────────────────────────────────────────────────────
//
//  Below `sm` the table becomes cards with full-width buttons.
//
//  ⚠ A NOTE ON MUI SIZING, LEARNED THE HARD WAY
//  `width: 1` in the `sx` prop is not one pixel — MUI reads a number between 0
//  and 1 as a FRACTION, so it renders 100%. A 1px divider written that way
//  spanned the whole toolbar and pushed the filters onto two rows. Pixel values
//  in here are strings: '1px'.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TextField, Chip, Button, Alert, Divider, LinearProgress,
  MenuItem, Select, IconButton, Snackbar, Dialog, DialogTitle, DialogContent,
  DialogActions, useMediaQuery, useTheme, InputAdornment, Tooltip, Fade,
  Checkbox, CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import BadgeIcon from '@mui/icons-material/Badge';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SearchIcon from '@mui/icons-material/Search';
import LockIcon from '@mui/icons-material/Lock';
import BoltIcon from '@mui/icons-material/Bolt';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import {
  renewalsSummary, renewalsList, renewalsCreate, renewalsClaim, renewalsUpdate,
  eligibilityCheck, apiConfigNhia,
} from '../BaseURL';
import EligibilityOverview from './EligibilityOverview';

const PRIMARY = '#1A9B7F';
const PRIMARY_DARK = '#137A64';
const INK = '#0F172A';
const MUTED = '#64748B';
const FAINT = '#94A3B8';
const LINE = '#E7EBF0';
const SOFT = '#F6F8FA';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
//  Two shadow levels only: resting, and lifted. Everything else is a border.
const SHADOW = '0 1px 2px rgba(15,23,42,.04), 0 1px 3px rgba(15,23,42,.03)';
const SHADOW_UP = '0 4px 14px rgba(15,23,42,.07), 0 1px 3px rgba(15,23,42,.04)';
const CARD = {
  borderRadius: 3,
  border: `1px solid ${LINE}`,
  boxShadow: SHADOW,
  background: '#fff',
};

const TONE = {
  pending:     { bg: '#FFF7ED', fg: '#B45309', bd: '#FDBA74', label: 'Pending' },
  in_progress: { bg: '#EFF6FF', fg: '#1D4ED8', bd: '#93C5FD', label: 'Being worked' },
  renewed:     { bg: '#ECFDF3', fg: '#15803D', bd: '#86EFAC', label: 'Renewed' },
  failed:      { bg: '#FEF2F2', fg: '#B91C1C', bd: '#FCA5A5', label: 'Could not renew' },
  //  Not a register status — derived from NHIA's start date having arrived.
  eligible:    { bg: '#ECFEFF', fg: '#0E7490', bd: '#67E8F9', label: 'Eligible for CCC' },
};

//  Fixed list so the failures stay countable — free text would make "why is
//  this campaign stalling" unanswerable.
const REASONS = [
  'Number not found',
  'Wrong number given',
  'Already active — no renewal needed',
  'Needs to attend in person',
  'Could not reach the member',
  'Network / USSD failure',
  'Other',
];

const NHIS_DIGITS = 8;

const currentUser = () => {
  try { return JSON.parse(localStorage.getItem('userInfo') || '{}')?.name || ''; }
  catch { return ''; }
};

//  Grouped 4-4 for reading aloud. Stored bare; presentation only.
const pretty = (n) => {
  const s = String(n || '');
  return s.length === NHIS_DIGITS ? `${s.slice(0, 4)} ${s.slice(4)}` : s;
};

//  A leading dot so the state reads at a glance even where colour alone would
//  not — the dot is the same hue, the label says it in words.
const StatusChip = ({ status }) => {
  const t = TONE[status] || TONE.pending;
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: .6,
      pl: .9, pr: 1.1, py: .35, borderRadius: 999,
      fontSize: 11, fontWeight: 700, letterSpacing: .1, whiteSpace: 'nowrap',
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
    }}>
      <Box component="span" sx={{
        width: 6, height: 6, borderRadius: '50%', background: t.fg, flexShrink: 0,
      }} />
      {t.label}
    </Box>
  );
};

/*  ── WHAT HAPPENED, UNDER THE STATUS ──────────────────────────────────────────
 *  One line saying where this renewal stands with NHIA:
 *
 *    went through    the CCC and the folder — the proof it is done
 *    eligible now    it has come alive and is waiting to be put through
 *    starts later    the date it becomes active, and how long until then
 *    did not work    the reason, in the register's words or NHIA's
 */
const day10 = (v) => String(v || '').slice(0, 10);
const daysUntil = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const target = new Date(y, m - 1, d);
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((target - now) / 86400000);
};
const NhiaNote = ({ r, size = 11 }) => {
  const line = (color, children, weight = 600) => (
    <Typography sx={{ fontSize: size, color, fontWeight: weight, mt: .5, lineHeight: 1.35 }}>
      {children}
    </Typography>
  );
  if (r.went_through) {
    return line(TONE.renewed.fg, (
      <>CCC <Box component="span" sx={{ fontFamily: MONO, fontWeight: 800 }}>{r.nhia_ccc}</Box>
        {r.patient_id ? <> · folder <Box component="span" sx={{ fontFamily: MONO }}>{r.patient_id}</Box></> : null}
      </>
    ));
  }
  const start = day10(r.nhia_start);
  if (r.eligible_now) {
    return line(PRIMARY_DARK, `Active since ${start} — ready for its CCC`, 700);
  }
  if (r.nhia_outcome === 'starts_later' && start) {
    const n = daysUntil(start);
    return line(TONE.pending.fg,
      `Becomes active ${start}${n != null && n > 0 ? ` · in ${n} day${n === 1 ? '' : 's'}` : ''}`);
  }
  if (r.status === 'failed' || ['not_found', 'no_ccc', 'failed'].includes(r.nhia_outcome)) {
    const why = r.reason
      || (r.nhia_outcome === 'not_found' ? 'NHIA does not know this card'
        : r.nhia_outcome === 'no_ccc' ? 'NHIA issued no CCC'
          : r.nhia_message || null);
    const nhiaSaid = r.nhia_status && r.nhia_status.toUpperCase() !== String(why || '').toUpperCase()
      ? ` · NHIA: ${r.nhia_status}` : '';
    return why ? line(TONE.failed.fg, `${why}${nhiaSaid}`, 500) : null;
  }
  return null;
};

/*  ── WHEN A NUMBER CAME IN: THE DATE AND THE TIME ────────────────────────────
 *
 *  This column used to say "Today" and nothing else, which is useless the
 *  moment more than a handful of numbers are taken in a day: you cannot tell
 *  the one written at 7am from the one written at 4pm, cannot tell what a shift
 *  actually collected, and cannot settle a question about when a member gave
 *  their card. So the exact date and clock time are shown, and "2 hours ago"
 *  moves underneath as the at-a-glance reading.
 *
 *  ⚠ THE TIMESTAMP IS READ OUT OF THE STRING, NOT THROUGH `new Date()`.
 *
 *  MySQL stores local wall-clock time. The driver hands it over as
 *  '2026-09-29T05:07:14.000Z' — the right numbers, wrongly labelled UTC. Pass
 *  that to `new Date()` and a browser one hour east renders 06:07 for a number
 *  taken at 05:07. Ghana is GMT so it looks fine here and would quietly drift
 *  on any server or laptop set to another zone. Taking the digits literally is
 *  correct everywhere.                                                        */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

//  Today as YYYY-MM-DD in the viewer's own day, for naming an export.
const localToday = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const stampOf = (raw) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(raw || ''));
  if (!m) return null;
  const [, y, mo, d, hh, mi] = m.map(Number);
  const h12 = ((hh + 11) % 12) + 1;
  const now = new Date();
  const when = new Date(y, mo - 1, d, hh, mi);
  const sameDay = now.getFullYear() === y && now.getMonth() === mo - 1 && now.getDate() === d;
  const yd = new Date(now); yd.setDate(now.getDate() - 1);
  const isYesterday = yd.getFullYear() === y && yd.getMonth() === mo - 1 && yd.getDate() === d;

  //  The year is dropped when it is this one — it is the same on every row and
  //  only takes width from the part that differs.
  const dateText = `${d} ${MONTHS[mo - 1]}${y === now.getFullYear() ? '' : ` ${y}`}`;
  const timeText = `${h12}:${String(mi).padStart(2, '0')} ${hh < 12 ? 'am' : 'pm'}`;

  const mins = Math.floor((now - when) / 60000);
  let ago;
  if (mins < 0) ago = dateText;
  else if (mins < 1) ago = 'just now';
  else if (mins < 60) ago = `${mins} min ago`;
  else if (mins < 60 * 24) {
    const h = Math.floor(mins / 60);
    ago = `${h} hour${h === 1 ? '' : 's'} ago`;
  } else {
    const days = Math.floor(mins / 1440);
    ago = days < 31 ? `${days} day${days === 1 ? '' : 's'} ago`
      : `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? '' : 's'} ago`;
  }

  return {
    dateText, timeText, ago, sameDay, isYesterday,
    //  Whole thing, for a tooltip and for the export.
    full: `${d} ${MONTHS[mo - 1]} ${y}, ${timeText}`,
    iso: `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}`,
  };
};

/*  ── DECLARED AT MODULE SCOPE, NOT INSIDE THE COMPONENT ─────────────────────
 *  A component declared in a render body is a new function every render, and
 *  React compares types by identity — so the whole subtree was being unmounted
 *  and rebuilt on every keystroke and every poll. On an input that costs you
 *  the caret (see MembershipSettings, where it made the fields unusable); here
 *  it only threw away the hover transition and did needless DOM work, but it
 *  is the same defect and it is fixed the same way. Reads nothing but its
 *  props and the module constants above.  */
// ── the two headline buckets ─────────────────────────────────────────────
const Bucket = ({ tone, icon, label, value, sub, active, onClick }) => {
  const c = TONE[tone];
  return (
    <Box component="button" type="button" onClick={onClick} aria-pressed={!!active} sx={{
      flex: 1, minWidth: 0, textAlign: 'left', font: 'inherit', cursor: 'pointer',
      p: { xs: 1.75, md: 2 }, borderRadius: 3, position: 'relative', overflow: 'hidden',
      background: active ? `linear-gradient(160deg, #fff 0%, ${c.bg} 100%)` : '#fff',
      border: `1px solid ${active ? c.bd : LINE}`,
      boxShadow: active ? SHADOW_UP : SHADOW,
      transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
      '&:hover': { borderColor: c.bd, transform: 'translateY(-2px)', boxShadow: SHADOW_UP },
      '&:focus-visible': { outline: `2px solid ${c.fg}`, outlineOffset: 2 },
      //  A thin accent along the top marks the selected bucket without the
      //  whole card shouting.
      '&::before': {
        content: '""', position: 'absolute', left: 0, right: 0, top: 0, height: 3,
        background: c.fg, opacity: active ? 1 : 0, transition: 'opacity .18s ease',
      },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: 2, display: 'grid', placeItems: 'center',
          color: c.fg, background: c.bg, border: `1px solid ${c.bd}`, flexShrink: 0,
        }}>{icon}</Box>
        <Typography noWrap sx={{
          fontSize: 10.5, fontWeight: 800, letterSpacing: .7,
          textTransform: 'uppercase', color: c.fg,
        }}>{label}</Typography>
      </Box>
      <Typography sx={{
        fontSize: { xs: 30, md: 36 }, fontWeight: 800, lineHeight: 1,
        color: INK, letterSpacing: -1.2, fontVariantNumeric: 'tabular-nums',
      }}>{Number(value || 0).toLocaleString()}</Typography>
      <Typography noWrap sx={{ fontSize: 11.5, color: MUTED, mt: .75 }}>
        {sub || ' '}
      </Typography>
    </Box>
  );
};

export default function MembershipManagement() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const me = useMemo(currentUser, []);
  const numberRef = useRef(null);

  const [campaign, setCampaign] = useState('');
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('pending');
  const [mine, setMine] = useState(false);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  /*  ── PAGING AND SORTING HAPPEN ON THE SERVER ────────────────────────────
   *  The register is built for ten thousand numbers. The old screen asked for
   *  200 and drew whatever came back, so past row 200 the rest simply were not
   *  there — no page control, no hint that anything was missing. Now the list
   *  asks for one page at a time and says which page it is on.                */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState('');          // '' = the fair default order
  const [dir, setDir] = useState('ASC');

  //  Numbers ticked for a bulk action, by row id.
  const [picked, setPicked] = useState(() => new Set());
  const [bulk, setBulk] = useState(null);        // { done, total, label }
  const [bulkFail, setBulkFail] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [num, setNum] = useState('');
  const [saving, setSaving] = useState(false);
  const [failing, setFailing] = useState(null);
  const [busyIds, setBusyIds] = useState(() => new Set());
  //  What NHIA said about the number just marked renewed.
  const [checkResult, setCheckResult] = useState(null);
  //  Bumped so the eligibility panel re-reads after a check.
  const [eligKey, setEligKey] = useState(0);

  const complete = num.length === NHIS_DIGITS;

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(renewalsSummary, {
        params: campaign ? { campaign } : {},
      });
      setSummary(data);
      if (!campaign && data.campaign) setCampaign(data.campaign);
    } catch (e) { setError(e?.response?.data?.message || e.message); }
  }, [campaign]);

  //  The filters every read shares, so the list, the count and the export can
  //  never disagree about what is being looked at.
  const filters = useMemo(() => ({
    campaign: campaign || undefined,
    status: status || undefined,
    mine: mine && me ? me : undefined,
    q: q || undefined,
    sort: sort || undefined,
    dir: sort ? dir : undefined,
  }), [campaign, status, mine, q, sort, dir, me]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(renewalsList, {
        params: { ...filters, limit: pageSize, offset: page * pageSize },
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setLoading(false); }
  }, [filters, page, pageSize]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => {
    const t = setTimeout(loadRows, 250);
    return () => clearTimeout(t);
  }, [loadRows]);

  /*  Change the view and you are looking at a different list, so page 4 of the
   *  old one is meaningless — and so is a selection made on it. Both are
   *  dropped rather than carried into rows the user never saw.                */
  useEffect(() => {
    setPage(0);
    setPicked(new Set());
  }, [campaign, status, mine, q, sort, dir, pageSize]);

  const save = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!complete) { numberRef.current?.focus(); return; }
    setSaving(true); setError(null);
    try {
      const { data } = await axios.post(renewalsCreate, {
        membership_no: num, campaign: campaign || undefined, user: me,
      });
      setToast({
        kind: 'ok',
        text: `${pretty(num)} added${data.row?.full_name ? ` — ${data.row.full_name}` : ''}`,
      });
      setNum('');
      await Promise.all([loadSummary(), loadRows()]);
    } catch (err) {
      const r = err?.response;
      setToast({
        kind: r?.data?.code === 'ALREADY_RECORDED' ? 'warn' : 'error',
        text: r?.data?.message || err.message,
      });
      if (r?.data?.code === 'ALREADY_RECORDED') setNum('');
    } finally {
      setSaving(false);
      setTimeout(() => numberRef.current?.focus(), 0);
    }
  }, [num, complete, campaign, me, loadSummary, loadRows]);

  const claim = useCallback(async (count) => {
    if (!me) { setToast({ kind: 'error', text: 'No signed-in user to assign these to.' }); return; }
    try {
      const { data } = await axios.post(renewalsClaim, {
        campaign: campaign || undefined, user: me, count,
      });
      setToast({
        kind: data.claimed ? 'ok' : 'warn',
        text: data.claimed ? `${data.claimed} assigned to you.`
          : 'Nothing left to take — every number has been started.',
      });
      if (data.claimed) { setStatus('in_progress'); setMine(true); }
      await Promise.all([loadSummary(), loadRows()]);
    } catch (e) {
      setToast({ kind: 'error', text: e?.response?.data?.message || e.message });
    }
  }, [campaign, me, loadSummary, loadRows]);

  const mark = useCallback(async (row, next, reason) => {
    setBusyIds((s) => new Set(s).add(row.id));
    try {
      await axios.put(`${renewalsUpdate}/${row.id}`, {
        status: next, user: me, ...(reason !== undefined ? { reason } : {}),
      });
      if (next === 'renewed') {
        setToast({ kind: 'ok', text: `${pretty(row.membership_no)} moved to Renewed` });
      }
      await Promise.all([loadSummary(), loadRows()]);
    } catch (e) {
      setToast({ kind: 'error', text: e?.response?.data?.message || e.message });
    } finally {
      setBusyIds((s) => { const n = new Set(s); n.delete(row.id); return n; });
    }
  }, [me, loadSummary, loadRows]);

  /*  ── MARKING RENEWED ASKS NHIA WHETHER IT WORKED ─────────────────────────
   *
   *  "Renewed" is a claim about what we did; only the eligibility service can
   *  say whether the membership actually works. So the button does both: it
   *  asks, records the whole answer, and lets the answer decide what the row
   *  becomes — including the case this feature exists for, where the renewal
   *  succeeded but does not come alive for another three weeks.
   *
   *  ── THE CALL IS MADE FROM HERE FIRST, AND THAT IS ON PURPOSE ────────────
   *
   *  Record ▸ CCC has always called NHIA straight from the browser, so that
   *  path is known to work from inside the facility. The server can make the
   *  same call — better, because the key and secret never leave it — but only
   *  if the server itself can reach elig.nhia.gov.gh, which is a question about
   *  somebody's firewall. So: try the browser, and if it cannot get through,
   *  post without a response and let the server try its own way. Either way the
   *  answer is recorded once, by the server.                                  */
  //  One number, start to finish. Returns what the server recorded. Deliberately
  //  free of any screen state so it can be driven one row at a time OR in a
  //  loop by the bulk action below.
  const checkOne = useCallback(async (row) => {
    let nhia = null;
    try {
      const { data: cfgRes } = await axios.get(apiConfigNhia);
      const c = cfgRes?.config;
      if (c?.base_url && c?.endpoint) {
        const r = await fetch(`${c.base_url}${c.endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-nhia-apikey': c.api_key,
            'x-nhia-apisecret': c.api_secret,
          },
          body: JSON.stringify({ CardType: 'NHISCARD', CardNo: row.membership_no }),
        });
        //  A 4xx is an ANSWER — "NHIA refuses this card" — and is recorded
        //  rather than treated as an outage.
        nhia = await r.json().catch(() => null);
      }
    } catch {
      //  Browser could not reach NHIA. Fall through with nhia = null and let
      //  the server attempt it.
    }

    const { data } = await axios.post(eligibilityCheck, {
      renewal_id: row.id,
      card_no: row.membership_no,
      card_type: 'NHISCARD',
      campaign: campaign || undefined,
      user: me,
      ...(nhia ? { response: nhia } : {}),
    });
    return data;
  }, [campaign, me]);

  const renewWithCheck = useCallback(async (row) => {
    setBusyIds((s) => new Set(s).add(row.id));
    setCheckResult(null);
    try {
      const data = await checkOne(row);
      setCheckResult({
        row, outcome: data.outcome, result: data.row, activation: data.activation,
      });
      await Promise.all([loadSummary(), loadRows()]);
      setEligKey((k) => k + 1);
    } catch (e) {
      setToast({ kind: 'error', text: e?.response?.data?.message || e.message });
    } finally {
      setBusyIds((s) => { const n = new Set(s); n.delete(row.id); return n; });
    }
  }, [checkOne, loadSummary, loadRows]);

  /*  ── SEVERAL NUMBERS AT ONCE ─────────────────────────────────────────────
   *
   *  A worker takes twenty numbers and phones them one after another. Marking
   *  each one meant a round trip and a dialog, twenty times. Ticking them and
   *  pressing once does the same work without the twenty interruptions.
   *
   *  ONE AT A TIME, ON PURPOSE. Twenty parallel requests to NHIA is a small
   *  burst against somebody else's service and makes a rate limit look like
   *  twenty failures. Sequential is barely slower in practice and the progress
   *  count means the wait is legible.
   *
   *  Each answer is still recorded individually by the server, so a member who
   *  turns out to be inactive is marked that way rather than swept into a
   *  blanket "renewed" — the whole point of asking NHIA at all.               */
  const bulkRenew = useCallback(async () => {
    const list = rows.filter((r) => picked.has(r.id) && !r.went_through);
    if (!list.length) return;
    setBulk({ done: 0, total: list.length });
    const tally = { active: 0, later: 0, failed: 0, error: 0 };
    let stoppedAt = null;   // the limit message, if the ceiling ended the run
    let ran = 0;
    for (let i = 0; i < list.length; i += 1) {
      try {
        const data = await checkOne(list[i]);
        if (data.outcome === 'active') tally.active += 1;
        else if (data.outcome === 'starts_later') tally.later += 1;
        else tally.failed += 1;
      } catch (e) {
        /*  ── THE CEILING ENDS THE RUN, IT DOES NOT FAIL EACH ROW ──────────
         *  A 429 means the server will refuse every remaining number for the
         *  same reason. Carrying on would turn one limit into nineteen more
         *  refused calls and a tally that reads as nineteen broken
         *  memberships.  */
        if (e?.response?.status === 429) {
          stoppedAt = e.response.data?.message || 'The renewal limit has been reached.';
          break;
        }
        tally.error += 1;
      }
      ran = i + 1;
      setBulk({ done: ran, total: list.length });
    }
    setBulk(null);
    setPicked(new Set());
    await Promise.all([loadSummary(), loadRows()]);
    setEligKey((k) => k + 1);

    if (stoppedAt) {
      setToast({
        kind: 'warn',
        text: `Stopped after ${ran} of ${list.length}. ${stoppedAt}`,
      });
      return;
    }
    const parts = [
      tally.active && `${tally.active} issued a CCC`,
      tally.later && `${tally.later} not live yet`,
      tally.failed && `${tally.failed} refused by NHIA`,
      tally.error && `${tally.error} could not be reached`,
    ].filter(Boolean);
    setToast({
      kind: tally.active ? 'ok' : 'warn',
      text: `${list.length} checked — ${parts.join(', ')}.`,
    });
  }, [rows, picked, checkOne, loadSummary, loadRows]);

  //  Writing off a batch for one shared reason — "could not reach the member"
  //  after a day of trying is the common one.
  const bulkWriteOff = useCallback(async (reason) => {
    const list = rows.filter((r) => picked.has(r.id) && !r.went_through);
    if (!list.length) return;
    setBulkFail(false);
    setBulk({ done: 0, total: list.length });
    let ok = 0;
    for (let i = 0; i < list.length; i += 1) {
      try {
        await axios.put(`${renewalsUpdate}/${list[i].id}`,
          { status: 'failed', user: me, reason });
        ok += 1;
      } catch { /* counted by omission */ }
      setBulk({ done: i + 1, total: list.length });
    }
    setBulk(null);
    setPicked(new Set());
    await Promise.all([loadSummary(), loadRows()]);
    setToast({
      kind: ok === list.length ? 'ok' : 'warn',
      text: `${ok} of ${list.length} written off — ${reason}.`,
    });
  }, [rows, picked, me, loadSummary, loadRows]);

  /*  ── EXPORT ──────────────────────────────────────────────────────────────
   *  Everything the current filter matches, not just the page on screen —
   *  a spreadsheet of 50 rows when the tab says 3,000 is a trap. Pulled in
   *  500-row pages because that is the server's ceiling.                      */
  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const all = [];
      const PAGE = 500;
      for (let off = 0; off < total && all.length < 20000; off += PAGE) {
        const { data } = await axios.get(renewalsList, {
          params: { ...filters, limit: PAGE, offset: off },
        });
        const got = data.rows || [];
        all.push(...got);
        if (got.length < PAGE) break;
      }
      const cell = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
      const head = ['NHIS number', 'Name', 'Status', 'Taken on', 'Taken by',
        'Assigned to', 'Attempts', 'Renewed on', 'CCC', 'Folder',
        'Active from', 'Expires', 'Reason'];
      const body = all.map((r) => [
        r.membership_no, r.full_name, (TONE[r.status] || {}).label || r.status,
        stampOf(r.created_at)?.iso || '', r.recorded_by, r.assigned_to, r.attempts,
        stampOf(r.renewed_at)?.iso || '', r.nhia_ccc, r.patient_id,
        day10(r.nhia_start), day10(r.nhia_end), r.reason,
      ].map(cell).join(','));
      const csv = [head.map(cell).join(','), ...body].join('\r\n');
      //  BOM so Excel opens it as UTF-8 and Ghanaian names keep their letters.
      const url = URL.createObjectURL(
        new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `nhis-renewals-${campaign || 'all'}`
        + `${status ? `-${status}` : ''}-${localToday()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setToast({ kind: 'ok', text: `${all.length.toLocaleString()} rows exported.` });
    } catch (e) {
      setToast({ kind: 'error', text: e?.response?.data?.message || e.message });
    } finally { setExporting(false); }
  }, [filters, total, campaign, status]);

  /*  Rows a bulk action can touch. A completed renewal is excluded everywhere —
   *  its CCC and folder exist and the server refuses to change it, so offering
   *  a tick box that cannot do anything is a lie the screen tells.            */
  const selectable = useMemo(() => rows.filter((r) => !r.went_through), [rows]);
  const allPicked = selectable.length > 0
    && selectable.every((r) => picked.has(r.id));
  const pickedCount = rows.filter((r) => picked.has(r.id) && !r.went_through).length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  //  First click sorts, clicking the same column again flips it.
  const toggleSort = useCallback((key) => {
    setSort((cur) => {
      if (cur === key) {
        setDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
        return cur;
      }
      //  Newest-first is what you want the moment you ask for dates at all.
      setDir(key === 'taken' ? 'DESC' : 'ASC');
      return key;
    });
  }, []);

  const copyNumber = useCallback(async (n) => {
    try {
      await navigator.clipboard.writeText(String(n));
      setToast({ kind: 'ok', text: `${pretty(n)} copied` });
    } catch {
      //  Clipboard is blocked without https or permission. Not worth an error.
    }
  }, []);

  /*  The ceiling and what is left of it, off the same summary the counts come
   *  from — so the allowance can never be a refresh behind the numbers beside
   *  it. An install without migration 073 sends neither, and the defaults the
   *  server falls back to still arrive here, so this reads the same either way.
   *  `left` is null for an uncapped window, which is NOT the same as 0.       */
  const limits = useMemo(() => {
    const s = summary?.settings || {};
    const u = summary?.usage || {};
    return {
      enabled: !!s.limits_enabled,
      cap: Number(s.max_per_day || 0),
      //  RENEWALS THAT WENT THROUGH — a CCC was issued. Attempts are carried
      //  separately and are not what the ceiling is measured against.
      used: Number(u.used?.day || 0),
      tried: Number(u.tried?.day || 0),
      left: u.left?.day ?? null,
      hourLeft: u.left?.hour ?? null,
      target: Number(s.daily_target || 0),
    };
  }, [summary]);

  const t = summary?.totals || {};
  const stillToDo = Number(t.pending || 0) + Number(t.in_progress || 0);
  const done = Number(t.renewed || 0);
  const failed = Number(t.failed || 0);
  //  Percentage of everything SETTLED — renewed counts, and so does a number
  //  written off, because neither is still waiting on anybody.
  const settled = done + failed;
  const grand = stillToDo + settled;
  const pct = grand > 0 ? Math.round((done / grand) * 100) : 0;


  const actions = (r, big) => {
    const busy = busyIds.has(r.id);
    /*  WENT THROUGH: a CCC was issued and a folder and visit exist. Final — no
     *  Undo. The server refuses it too (409 LOCKED); this only stops offering a
     *  button that cannot work.                                               */
    if (r.went_through) {
      return (
        <Tooltip title="NHIA issued a CCC and the folder and visit were created. This cannot be undone.">
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: .6, px: 1.25, py: .6,
            borderRadius: 2, fontSize: 12, fontWeight: 700,
            color: TONE.renewed.fg, background: TONE.renewed.bg,
            border: `1px solid ${TONE.renewed.bd}`,
            width: big ? '100%' : 'auto', justifyContent: 'center',
          }}>
            <LockIcon sx={{ fontSize: 15 }} /> Completed
          </Box>
        </Tooltip>
      );
    }
    //  ELIGIBLE NOW: renewed earlier, "not live yet", and its start date has
    //  arrived. One click puts it through NHIA for its CCC.
    if (r.eligible_now) {
      return (
        <Button size={big ? 'medium' : 'small'} variant="contained" disableElevation
          disabled={busy} startIcon={<BoltIcon />} fullWidth={big}
          onClick={() => renewWithCheck(r)}
          sx={{
            background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
            fontWeight: 700, textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap',
            px: 1.75, boxShadow: `0 2px 6px ${PRIMARY}40`,
            '&:hover': { background: PRIMARY_DARK },
          }}>
          {busy ? 'Asking NHIA…' : 'Generate CCC'}
        </Button>
      );
    }
    if (r.status === 'renewed') {
      return (
        <Button size="small" disabled={busy} startIcon={<UndoIcon />}
          onClick={() => mark(r, 'pending')} fullWidth={big}
          sx={{ color: MUTED, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
          Undo
        </Button>
      );
    }
    return (
      <Box sx={{
        display: 'flex', gap: .75, width: big ? '100%' : 'auto',
        justifyContent: big ? 'stretch' : 'flex-end', alignItems: 'center',
      }}>
        <Button size={big ? 'medium' : 'small'} variant="contained" disableElevation
          disabled={busy} startIcon={<CheckCircleIcon />}
          endIcon={!big && <ArrowForwardIcon sx={{ fontSize: 15 }} />}
          onClick={() => renewWithCheck(r)} fullWidth={big}
          sx={{
            background: `linear-gradient(135deg, #16A34A 0%, ${TONE.renewed.fg} 100%)`,
            fontWeight: 700, textTransform: 'none',
            borderRadius: 2, whiteSpace: 'nowrap', px: 1.75,
            boxShadow: '0 2px 6px rgba(21,128,61,.25)',
            transition: 'box-shadow .15s ease, transform .12s ease',
            '&:hover': { background: '#116534', boxShadow: '0 4px 12px rgba(21,128,61,.32)' },
            '&:active': { transform: 'scale(.98)' },
            '& .MuiButton-endIcon': { transition: 'transform .15s ease' },
            '&:hover .MuiButton-endIcon': { transform: 'translateX(2px)' },
          }}>
          {busy ? 'Asking NHIA…' : 'Mark renewed'}
        </Button>
        <Tooltip title="Could not renew">
          <span>
            <IconButton size="small" disabled={busy} onClick={() => setFailing(r)}
              sx={{
                color: FAINT, border: `1px solid ${LINE}`, borderRadius: 2,
                '&:hover': { color: TONE.failed.fg, borderColor: TONE.failed.bd,
                  background: TONE.failed.bg },
              }}>
              <CloseIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  /*  ── SIDE BY SIDE, NOT STACKED ────────────────────────────────────────────
   *
   *  Everything used to sit in one column: entry, then counts, then progress,
   *  then the NHIA panel, and only then the table — so the list, which is the
   *  thing being worked, started below the fold and every action meant
   *  scrolling back up. Meanwhile several hundred pixels sat empty either side.
   *
   *  The controls are a rail now and the table takes the rest. The rail STICKS,
   *  so the number box and the counts stay put while the list scrolls under
   *  them — a clerk taking numbers never loses the field, and somebody working
   *  the list never loses sight of how many are left.
   *
   *  Below `lg` it collapses back to one column, because a 360px rail beside a
   *  table does not fit on a laptop and certainly not on a phone.            */
  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1680, mx: 'auto' }}>

      {/* ── header ──────────────────────────────────────────────────────── */}
      {/*  A soft band rather than bare text on the page — it gives the screen a
           top edge and a place for the campaign and the person working it,
           which were a run-on line of grey before.  */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.75, mb: 2.5, flexWrap: 'wrap',
        p: { xs: 1.75, md: 2.25 }, borderRadius: 3.5, position: 'relative', overflow: 'hidden',
        background: `linear-gradient(120deg, ${PRIMARY}14 0%, #34D39912 45%, #fff 100%)`,
        border: `1px solid ${PRIMARY}26`,
      }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 3, display: 'grid', placeItems: 'center',
          background: `linear-gradient(135deg, ${PRIMARY} 0%, #34D399 100%)`,
          color: '#fff', flexShrink: 0, boxShadow: `0 6px 16px ${PRIMARY}40`,
        }}><BadgeIcon sx={{ fontSize: 24 }} /></Box>
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Typography sx={{
            fontSize: { xs: 19, md: 22 }, fontWeight: 800, color: INK,
            lineHeight: 1.2, letterSpacing: -.4,
          }}>Membership Management</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mt: .5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>Free NHIS renewal</Typography>
            {campaign && (
              <Box component="span" sx={{
                px: .9, py: .15, borderRadius: 999, fontSize: 11, fontWeight: 700,
                color: PRIMARY_DARK, background: '#fff', border: `1px solid ${PRIMARY}40`,
              }}>{campaign} campaign</Box>
            )}
            {me && (
              <Box component="span" sx={{
                px: .9, py: .15, borderRadius: 999, fontSize: 11, fontWeight: 600,
                color: MUTED, background: '#fff', border: `1px solid ${LINE}`,
              }}>Working as {me}</Box>
            )}
          </Box>
        </Box>
        {(summary?.campaigns || []).length > 1 && (
          <Select size="small" value={campaign} onChange={(e) => setCampaign(e.target.value)}
            sx={{ minWidth: 104, borderRadius: 2 }}>
            {summary.campaigns.map((c) => (
              <MenuItem key={c.campaign} value={c.campaign}>{c.campaign}</MenuItem>
            ))}
          </Select>
        )}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => { loadSummary(); loadRows(); }}
            aria-label="Refresh"
            sx={{
              border: `1px solid ${LINE}`, borderRadius: 2.5, background: '#fff',
              width: 38, height: 38, boxShadow: SHADOW,
              '&:hover': { background: '#fff', borderColor: PRIMARY, color: PRIMARY },
            }}>
            <RefreshIcon sx={{ fontSize: 19, ...(loading && {
              animation: 'mm-spin .9s linear infinite',
              '@keyframes mm-spin': { to: { transform: 'rotate(360deg)' } },
            }) }} />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ══ two columns: controls on the left, the list on the right ══════ */}
      <Box sx={{
        display: 'grid', gap: 2, alignItems: 'start',
        gridTemplateColumns: { xs: '1fr', lg: '370px minmax(0, 1fr)' },
      }}>

      {/* ── LEFT RAIL ───────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'grid', gap: 1.5, alignContent: 'start',
        //  Sticky so the number box and the counts stay in view while the
        //  list scrolls beside them.
        position: { lg: 'sticky' }, top: { lg: 16 },
      }}>

      {/* ── take a number ───────────────────────────────────────────────── */}
      <Paper component="form" onSubmit={save} elevation={0} sx={{
        ...CARD, p: { xs: 1.75, md: 2.25 },
        borderColor: complete ? PRIMARY : LINE,
        boxShadow: complete ? `0 0 0 4px ${PRIMARY}1A, ${SHADOW_UP}` : SHADOW,
        transition: 'border-color .18s ease, box-shadow .18s ease',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Box sx={{
            width: 26, height: 26, borderRadius: 1.75, display: 'grid', placeItems: 'center',
            background: `${PRIMARY}1A`, color: PRIMARY,
          }}><AddIcon sx={{ fontSize: 17 }} /></Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontSize: 10.5, fontWeight: 800, color: PRIMARY, letterSpacing: .7,
              textTransform: 'uppercase', lineHeight: 1.2,
            }}>Take a number</Typography>
            <Typography sx={{ fontSize: 11.5, color: MUTED, lineHeight: 1.3 }}>
              The member's NHIS card number
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'stretch',
          flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            inputRef={numberRef} autoFocus={!isPhone} fullWidth
            value={pretty(num)}
            onChange={(e) => setNum(e.target.value.replace(/\D/g, '').slice(0, NHIS_DIGITS))}
            placeholder="0000 0000"
            inputProps={{
              inputMode: 'numeric', autoComplete: 'off',
              'aria-label': 'NHIS number, 8 digits',
              style: {
                fontSize: isPhone ? 28 : 27, fontWeight: 800, letterSpacing: 4,
                fontFamily: MONO, fontVariantNumeric: 'tabular-nums',
                textAlign: 'center', padding: isPhone ? '13px 8px' : '12px 8px',
                color: INK,
              },
            }}
            autoComplete="off" name="nhis-renewal-number"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{
                    px: 1, py: .25, borderRadius: 1.5, fontSize: 11, fontWeight: 800,
                    background: complete ? `${PRIMARY}1A` : '#F1F5F9',
                    color: complete ? PRIMARY : FAINT,
                  }}>{num.length}/{NHIS_DIGITS}</Box>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5, background: SOFT,
                transition: 'background .15s ease',
                '& fieldset': { borderColor: LINE },
                '&:hover fieldset': { borderColor: '#CBD5E1' },
                '&.Mui-focused': { background: '#fff' },
                '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: '1.5px' },
              },
            }}
          />
          <Button type="submit" variant="contained" disableElevation startIcon={<AddIcon />}
            disabled={!complete || saving} fullWidth={isPhone}
            sx={{
              background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
              fontWeight: 700, borderRadius: 2.5,
              minWidth: 120, flexShrink: 0, textTransform: 'none', fontSize: 15,
              boxShadow: complete ? `0 6px 16px ${PRIMARY}45` : 'none',
              transition: 'box-shadow .18s ease, transform .12s ease',
              '&:hover': { background: PRIMARY_DARK },
              '&:active': { transform: 'scale(.98)' },
              '&.Mui-disabled': { background: '#E2E8F0', color: '#94A3B8' },
            }}>
            {saving ? 'Adding…' : 'Add'}
          </Button>
        </Box>
        {/*  Eight small ticks under the field, filling as digits arrive — the
             count made visible, so nobody has to read "6/8" to know it is short. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1.25 }}>
          <Box aria-hidden sx={{ display: 'flex', gap: '3px' }}>
            {Array.from({ length: NHIS_DIGITS }).map((_, i) => (
              <Box key={i} sx={{
                width: 14, height: 4, borderRadius: 99,
                background: i < num.length ? PRIMARY : LINE,
                transition: 'background .12s ease',
              }} />
            ))}
          </Box>
          <Typography sx={{
            fontSize: 11.5, color: complete ? PRIMARY_DARK : FAINT,
            fontWeight: complete ? 700 : 500,
          }}>
            {complete ? 'Ready — press Enter or tap Add' : `${NHIS_DIGITS} digits, numbers only`}
          </Typography>
        </Box>
      </Paper>

      {/* ── buckets ─────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Bucket tone="pending" icon={<HourglassEmptyIcon sx={{ fontSize: 16 }} />}
          label="Pending" value={stillToDo}
          sub={t.in_progress ? `${t.in_progress} being worked` : 'still to renew'}
          active={status === 'pending' || status === 'in_progress'}
          onClick={() => { setStatus('pending'); setMine(false); }} />
        <Bucket tone="renewed" icon={<TaskAltIcon sx={{ fontSize: 16 }} />}
          label="Renewed" value={done}
          sub={t.renewed_today ? `${t.renewed_today} today` : 'completed'}
          active={status === 'renewed'}
          onClick={() => { setStatus('renewed'); setMine(false); }} />
      </Box>

      {/*  ── HOW FAR ALONG ──────────────────────────────────────────────
           The question anyone running a promotion actually asks. Two bare
           counts leave you doing the arithmetic yourself every time. */}
      {/*  ── ONE STRIP, THREE STATES ────────────────────────────────────
           A single green fill hid the written-off numbers inside "not done
           yet". Segmented, the bar says where every number went: renewed,
           written off, or still waiting — and the widths add up to the whole
           campaign, which is the only honest way to draw a proportion.  */}
      {grand > 0 && (() => {
        const seg = [
          { key: 'renewed', n: done, c: TONE.renewed.fg, label: 'Renewed' },
          { key: 'failed', n: failed, c: TONE.failed.fg, label: 'Written off' },
          { key: 'pending', n: stillToDo, c: '#F59E0B', label: 'Waiting' },
        ];
        return (
          <Paper elevation={0} sx={{ ...CARD, px: 2.25, py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 1.25 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontSize: 10.5, fontWeight: 800, letterSpacing: .7,
                  textTransform: 'uppercase', color: MUTED,
                }}>Campaign progress</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK, mt: .25 }}>
                  {done.toLocaleString()} of {grand.toLocaleString()} renewed
                </Typography>
              </Box>
              <Typography sx={{
                fontSize: 26, fontWeight: 800, color: TONE.renewed.fg, lineHeight: 1,
                letterSpacing: -.8, fontVariantNumeric: 'tabular-nums',
              }}>{pct}<Box component="span" sx={{ fontSize: 15, ml: .15 }}>%</Box></Typography>
            </Box>

            <Box role="img"
              aria-label={`${done} renewed, ${failed} written off, ${stillToDo} waiting, of ${grand}`}
              sx={{
                display: 'flex', height: 10, borderRadius: 99, overflow: 'hidden',
                background: '#F1F5F9', gap: '2px',
              }}>
              {seg.filter((s) => s.n > 0).map((s) => (
                <Box key={s.key} sx={{
                  width: `${(s.n / grand) * 100}%`, background: s.c,
                  transition: 'width .4s ease',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }} />
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1.75, mt: 1.25, flexWrap: 'wrap' }}>
              {seg.map((s) => (
                <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: .6 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: 2, background: s.c }} />
                  <Typography sx={{ fontSize: 11.5, color: MUTED }}>
                    {s.label}{' '}
                    <Box component="span" sx={{
                      color: INK, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    }}>{s.n.toLocaleString()}</Box>
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        );
      })()}

      {/*  ── TODAY'S ALLOWANCE ────────────────────────────────────────────
           Every renewal is a call to NHIA, and the facility sets a ceiling in
           Maintenance ▸ Settings. This is a read of what the SERVER has spent —
           the server refuses over the limit, this only makes the refusal stop
           being a surprise. Hidden when no ceiling is set, because a bar with
           no end is not telling anyone anything.  */}
      {limits.enabled && limits.cap > 0 && (
        <Paper elevation={0} sx={{
          ...CARD, px: 2.25, py: 1.75,
          ...(limits.left === 0 && {
            borderColor: TONE.failed.bd, background: TONE.failed.bg,
          }),
        }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
            <Typography sx={{
              fontSize: 10.5, fontWeight: 800, letterSpacing: .7,
              textTransform: 'uppercase', color: limits.left === 0 ? TONE.failed.fg : MUTED,
              flex: 1,
            }}>Today's allowance</Typography>
            <Typography sx={{
              fontSize: 13, fontWeight: 800, color: INK,
              fontVariantNumeric: 'tabular-nums',
            }}>{limits.used} / {limits.cap}</Typography>
          </Box>
          <Box sx={{ height: 8, borderRadius: 99, background: '#F1F5F9', overflow: 'hidden' }}>
            <Box sx={{
              width: `${Math.min(100, (limits.used / limits.cap) * 100)}%`, height: '100%',
              borderRadius: 99, transition: 'width .4s ease',
              background: limits.left === 0 ? TONE.failed.fg
                : limits.left <= Math.max(3, limits.cap * .2) ? '#F59E0B' : PRIMARY,
            }} />
          </Box>
          <Typography sx={{
            fontSize: 11.5, mt: .9, lineHeight: 1.5,
            color: limits.left === 0 ? TONE.failed.fg : MUTED,
          }}>
            {limits.left === 0
              ? 'Spent. No more renewals can be put through until midnight.'
              : <><strong>{limits.left}</strong> left today
                {limits.hourLeft != null && limits.hourLeft <= 3
                  ? ` · only ${limits.hourLeft} more this hour` : ''}</>}
          </Typography>

          {/*  Only when they differ. Attempts that produced no CCC renewed
               nobody and are NOT charged to the allowance — saying so here is
               what stops "101 / 70" reading as a broken counter. */}
          {limits.tried > limits.used && (
            <Typography sx={{ fontSize: 11, color: FAINT, mt: .35 }}>
              {limits.tried.toLocaleString()} tried ·{' '}
              {(limits.tried - limits.used).toLocaleString()} gave no CCC and were
              not counted
            </Typography>
          )}
        </Paper>
      )}

      {/*  What NHIA actually said. Renders nothing until a check has been run,
          so a facility that has not started does not see an empty panel. */}
      <EligibilityOverview campaign={campaign} refreshKey={eligKey} compact />

      </Box>{/* ── end of the left rail ─────────────────────────────────── */}

      {/* ── RIGHT: the list being worked ────────────────────────────────── */}
      <Box sx={{ minWidth: 0 }}>

      {/*  ── ONE CARD: THE FILTERS BELONG TO THE LIST ─────────────────────
           The chips used to float above an unframed table, so the page read
           as scattered parts. Now the tabs are the table's own header: pick a
           view, and the rows beneath are that view. Tabs rather than chips
           because exactly one is ever selected — which is what a tab says.  */}
      <Paper elevation={0} sx={{ ...CARD, overflow: 'hidden' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap',
        gap: { xs: 1, md: 1.5 }, px: { xs: 1.25, md: 1.75 }, pt: 1, pb: 0,
        borderBottom: `1px solid ${LINE}`, background: '#fff',
      }}>
        <Box role="tablist" aria-label="Which numbers" sx={{
          display: 'flex', gap: .25, overflowX: 'auto', minWidth: 0,
          flex: { xs: '1 1 100%', md: '0 1 auto' },
          '&::-webkit-scrollbar': { display: 'none' },
        }}>
          {[
            //  First, because it is the one with work waiting: renewed members
            //  whose membership has now come alive, ready for their CCC.
            ...(Number(t.eligible) > 0 || status === 'eligible'
              ? [{ v: 'eligible', label: 'Eligible for CCC', n: Number(t.eligible || 0), c: TONE.eligible.fg }]
              : []),
            { v: '', label: 'All', n: Number(t.total || 0), c: INK },
            { v: 'pending', label: 'Pending', n: Number(t.pending || 0), c: TONE.pending.fg },
            ...(Number(t.in_progress) > 0
              ? [{ v: 'in_progress', label: 'Being worked', n: Number(t.in_progress), c: TONE.in_progress.fg }]
              : []),
            { v: 'renewed', label: 'Renewed', n: done, c: TONE.renewed.fg },
            ...(failed > 0
              ? [{ v: 'failed', label: 'Could not renew', n: failed, c: TONE.failed.fg }]
              : []),
          ].map((tab) => {
            const on = status === tab.v;
            return (
              <Box key={tab.v || 'all'} component="button" type="button" role="tab"
                aria-selected={on}
                onClick={() => { setStatus(tab.v); setMine(false); }}
                sx={{
                  font: 'inherit', cursor: 'pointer', border: 0, background: 'transparent',
                  display: 'inline-flex', alignItems: 'center', gap: .75, whiteSpace: 'nowrap',
                  px: 1.25, py: 1.25, position: 'relative',
                  fontSize: 13, fontWeight: on ? 700 : 600, color: on ? INK : MUTED,
                  transition: 'color .15s ease',
                  '&:hover': { color: INK },
                  '&:focus-visible': { outline: `2px solid ${PRIMARY}`, outlineOffset: -2, borderRadius: 1 },
                  '&::after': {
                    content: '""', position: 'absolute', left: 8, right: 8, bottom: -1,
                    height: 2.5, borderRadius: 2, background: tab.c,
                    opacity: on ? 1 : 0, transition: 'opacity .15s ease',
                  },
                }}>
                {tab.label}
                <Box component="span" sx={{
                  px: .75, py: .05, borderRadius: 999, fontSize: 11, fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  background: on ? `${tab.c}14` : SOFT,
                  color: on ? tab.c : FAINT,
                }}>{tab.n.toLocaleString()}</Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ flex: 1, minWidth: 8, display: { xs: 'none', md: 'block' } }} />

        <Box sx={{
          display: 'flex', alignItems: 'center', gap: .75, pb: 1,
          flex: { xs: '1 1 100%', md: '0 0 auto' },
        }}>
          <Tooltip title="Assign the next 20 pending numbers to you">
            <span>
              <Button size="small" startIcon={<PlaylistAddCheckIcon />} onClick={() => claim(20)}
                disabled={!t.pending}
                sx={{
                  textTransform: 'none', fontWeight: 700, color: PRIMARY_DARK,
                  borderRadius: 2, px: 1.25, border: `1px solid ${PRIMARY}33`,
                  background: `${PRIMARY}0A`,
                  '&:hover': { background: `${PRIMARY}14` },
                }}>
                Take 20
              </Button>
            </span>
          </Tooltip>
          <Chip size="small" clickable label="Only mine"
            icon={mine ? <CheckCircleIcon sx={{ fontSize: '15px !important' }} /> : undefined}
            onClick={() => setMine((v) => !v)}
            sx={{
              fontWeight: 700, height: 30, borderRadius: 2,
              background: mine ? PRIMARY : '#fff',
              color: mine ? '#fff' : MUTED,
              border: `1px solid ${mine ? PRIMARY : LINE}`,
              '& .MuiChip-icon': { color: '#fff' },
              '&:hover': { background: mine ? PRIMARY_DARK : SOFT },
            }} />
          <TextField size="small" placeholder="Search a number…" value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: FAINT }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: { xs: 1, md: '0 1 220px' }, minWidth: 0,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2, background: SOFT, fontSize: 13, height: 32,
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: LINE },
                '&.Mui-focused': { background: '#fff' },
                '&.Mui-focused fieldset': { borderColor: PRIMARY },
              },
            }} />
        </Box>
      </Box>

      {/* ── the list ────────────────────────────────────────────────────── */}
      {/*  ── MEMBERS WAITING FOR THEIR CCC ─────────────────────────────────
           Said on every other view too, so a member who came alive today is
           not missed by somebody who never opens that tab.  */}
      {Number(t.eligible) > 0 && status !== 'eligible' && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
          px: { xs: 1.5, md: 2 }, py: 1.1,
          background: TONE.eligible.bg, borderBottom: `1px solid ${TONE.eligible.bd}`,
        }}>
          <BoltIcon sx={{ fontSize: 18, color: TONE.eligible.fg }} />
          <Typography sx={{ fontSize: 12.5, color: TONE.eligible.fg, flex: 1, minWidth: 180 }}>
            <strong>{Number(t.eligible).toLocaleString()}</strong>{' '}
            renewed member{Number(t.eligible) === 1 ? ' has' : 's have'} come alive and{' '}
            {Number(t.eligible) === 1 ? 'is' : 'are'} ready for a CCC.
          </Typography>
          <Button size="small" onClick={() => { setStatus('eligible'); setMine(false); }}
            sx={{
              textTransform: 'none', fontWeight: 700, color: '#fff', borderRadius: 2, px: 1.5,
              background: TONE.eligible.fg, '&:hover': { background: '#155E75' },
            }}>
            Show them
          </Button>
        </Box>
      )}

      {/*  ── WHAT IS TICKED, AND WHAT CAN BE DONE WITH IT ──────────────────
           Replaces the tab strip's quiet area only while a selection exists,
           so the screen is not carrying a toolbar nobody is using.  */}
      {pickedCount > 0 && !bulk && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          px: { xs: 1.5, md: 2 }, py: 1.1,
          background: `${PRIMARY}0D`, borderBottom: `1px solid ${PRIMARY}33`,
        }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: PRIMARY_DARK }}>
            {pickedCount} selected
          </Typography>
          <Button size="small" onClick={() => setPicked(new Set())}
            sx={{ textTransform: 'none', fontWeight: 600, color: MUTED, minWidth: 0 }}>
            Clear
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" onClick={() => setBulkFail(true)}
            startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2,
              color: TONE.failed.fg, borderColor: TONE.failed.bd,
              '&:hover': { borderColor: TONE.failed.fg, background: TONE.failed.bg },
            }}>
            Could not renew
          </Button>
          <Button size="small" variant="contained" disableElevation onClick={bulkRenew}
            startIcon={<CheckCircleIcon sx={{ fontSize: 17 }} />}
            sx={{
              background: `linear-gradient(135deg, #16A34A 0%, ${TONE.renewed.fg} 100%)`,
              textTransform: 'none', fontWeight: 700, borderRadius: 2,
              boxShadow: '0 2px 6px rgba(21,128,61,.25)',
              '&:hover': { background: '#116534' },
            }}>
            Renew {pickedCount}
          </Button>
        </Box>
      )}

      {/*  A batch is a wait measured in seconds per number, so it says where it
           has got to rather than freezing the screen.  */}
      {bulk && (
        <Box sx={{
          px: { xs: 1.5, md: 2 }, py: 1.25,
          background: `${PRIMARY}0D`, borderBottom: `1px solid ${PRIMARY}33`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: .75 }}>
            <CircularProgress size={15} sx={{ color: PRIMARY }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: PRIMARY_DARK }}>
              Asking NHIA · {bulk.done} of {bulk.total}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 11.5, color: MUTED }}>
              One at a time — please leave this open
            </Typography>
          </Box>
          <LinearProgress variant="determinate"
            value={bulk.total ? (bulk.done / bulk.total) * 100 : 0}
            sx={{
              height: 5, borderRadius: 99, background: `${PRIMARY}22`,
              '& .MuiLinearProgress-bar': { background: PRIMARY, borderRadius: 99 },
            }} />
        </Box>
      )}

      {/*  A thin bar under the tabs while rows load, so a slow list reads as
           working rather than empty.  */}
      <Box sx={{ height: 2, background: 'transparent', overflow: 'hidden' }}>
        {loading && <LinearProgress sx={{
          height: 2, background: 'transparent',
          '& .MuiLinearProgress-bar': { background: PRIMARY },
        }} />}
      </Box>

      {rows.length === 0 && !loading ? (
        <Box sx={{ px: 3, py: 7, textAlign: 'center' }}>
          <Box sx={{
            width: 64, height: 64, mx: 'auto', mb: 1.75, borderRadius: '50%',
            display: 'grid', placeItems: 'center',
            background: status === 'renewed' ? TONE.renewed.bg : SOFT,
            color: status === 'renewed' ? TONE.renewed.fg : '#CBD5E1',
            border: `1px dashed ${status === 'renewed' ? TONE.renewed.bd : '#CBD5E1'}`,
          }}>
            {status === 'renewed' ? <TaskAltIcon sx={{ fontSize: 30 }} />
              : <HourglassEmptyIcon sx={{ fontSize: 30 }} />}
          </Box>
          <Typography sx={{ fontSize: 14.5, color: INK, fontWeight: 700 }}>
            {status === 'renewed' && !q ? 'Nothing renewed yet'
              : total === 0 && !q ? 'Nothing here yet'
                : 'Nothing matches'}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: MUTED, mt: .5 }}>
            {status === 'renewed' && !q
              ? 'Work the pending list and they arrive here.'
              : total === 0 && !q
                ? 'Take a number on the left to start.'
                : 'Try a different search.'}
          </Typography>
        </Box>
      ) : isPhone ? (
        <Box sx={{ display: 'grid', gap: 1, p: 1.25, background: SOFT }}>
          {rows.map((r) => (
            <Fade in key={r.id}>
              <Paper elevation={0} sx={{
                ...CARD, p: 1.75, position: 'relative', overflow: 'hidden',
                '&::before': {
                  content: '""', position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: 3, background: (TONE[r.status] || TONE.pending).fg,
                },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: .5 }}>
                  <Typography sx={{
                    fontSize: 22, fontWeight: 800, letterSpacing: 2,
                    fontFamily: MONO, fontVariantNumeric: 'tabular-nums',
                    color: INK, flex: 1,
                  }}>{pretty(r.membership_no)}</Typography>
                  <StatusChip status={r.eligible_now ? 'eligible' : r.status} />
                </Box>
                {/*  Same date and time as the table, on one line. */}
                {(() => {
                  const s = stampOf(r.created_at);
                  return (
                    <Typography sx={{ fontSize: 11.5, color: MUTED, mb: 1.25 }}>
                      {s ? (
                        <>
                          <Box component="span" sx={{ fontWeight: 700, color: INK }}>
                            {s.dateText}, {s.timeText}
                          </Box>
                          {' · '}{s.ago}
                        </>
                      ) : 'Not recorded'}
                      {r.attempts > 0
                        ? ` · ${r.attempts} attempt${r.attempts === 1 ? '' : 's'}` : ''}
                    </Typography>
                  );
                })()}
                <Box sx={{ mt: -.75, mb: 1 }}><NhiaNote r={r} size={11.5} /></Box>
                <Divider sx={{ mb: 1.25, borderColor: LINE }} />
                {actions(r, true)}
              </Paper>
            </Fade>
          ))}
        </Box>
      ) : (
        //  Taller now that the counts are beside it rather than above it — the
        //  whole point of the rail was to give the list back its room.
        <TableContainer sx={{ maxHeight: { xs: '52vh', lg: 'calc(100vh - 300px)' } }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{
                  background: SOFT, borderBottom: `1px solid ${LINE}`, pl: 1.5,
                }}>
                  <Tooltip title={allPicked ? 'Clear selection' : 'Select this page'}>
                    <span>
                      <Checkbox size="small" checked={allPicked}
                        indeterminate={picked.size > 0 && !allPicked}
                        disabled={!selectable.length}
                        onChange={() => setPicked(allPicked ? new Set()
                          : new Set(selectable.map((r) => r.id)))}
                        sx={{ color: '#CBD5E1', '&.Mui-checked': { color: PRIMARY },
                          '&.MuiCheckbox-indeterminate': { color: PRIMARY } }} />
                    </span>
                  </Tooltip>
                </TableCell>
                {[
                  { l: 'NHIS number', a: 'left', k: 'number' },
                  { l: 'Status', a: 'left', k: 'status' },
                  { l: 'Taken', a: 'left', k: 'taken' },
                  { l: 'Recorded by', a: 'left', k: null },
                  { l: '', a: 'right', k: null },
                ].map((h, i) => {
                  const on = h.k && sort === h.k;
                  return (
                    <TableCell key={h.l || `c${i}`} align={h.a} sx={{
                      fontWeight: 700, fontSize: 10.5, letterSpacing: .6,
                      textTransform: 'uppercase', color: on ? PRIMARY_DARK : FAINT,
                      background: SOFT, borderBottom: `1px solid ${LINE}`, py: 1.1,
                      whiteSpace: 'nowrap',
                      ...(h.k && { cursor: 'pointer', userSelect: 'none',
                        '&:hover': { color: INK } }),
                      ...(h.l === 'Recorded by' && { display: { xs: 'none', xl: 'table-cell' } }),
                    }}
                      onClick={h.k ? () => toggleSort(h.k) : undefined}>
                      {h.l}
                      {h.k && (
                        <Box component="span" sx={{
                          display: 'inline-flex', verticalAlign: 'middle', ml: .3,
                          opacity: on ? 1 : .25, transition: 'opacity .15s ease',
                        }}>
                          {on && dir === 'DESC'
                            ? <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                            : <ArrowUpwardIcon sx={{ fontSize: 13 }} />}
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const tone = TONE[r.status] || TONE.pending;
                const s = stampOf(r.created_at);
                const on = picked.has(r.id);
                return (
                  <TableRow key={r.id} selected={on} sx={{
                    transition: 'background .12s ease',
                    '&:hover': { background: '#FAFBFC' },
                    '&.Mui-selected': { background: `${PRIMARY}0A` },
                    '&.Mui-selected:hover': { background: `${PRIMARY}14` },
                    '& td': { borderBottom: `1px solid ${LINE}` },
                    '&:last-of-type td': { borderBottom: 0 },
                    //  The row's state along its left edge — scannable down a
                    //  long list without reading a single chip.
                    '& td:first-of-type': { boxShadow: `inset 3px 0 0 ${tone.fg}` },
                  }}>
                    <TableCell padding="checkbox" sx={{ pl: 1.5 }}>
                      <Checkbox size="small" checked={on} disabled={!!r.went_through}
                        onChange={() => setPicked((set) => {
                          const n = new Set(set);
                          if (n.has(r.id)) n.delete(r.id); else n.add(r.id);
                          return n;
                        })}
                        sx={{ color: '#CBD5E1', '&.Mui-checked': { color: PRIMARY } }} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {/*  Click the number to copy it — it gets read into a
                          phone keypad all day, and retyping eight digits is
                          where a wrong renewal comes from.  */}
                      <Tooltip title="Click to copy" placement="right">
                        <Typography component="button" type="button"
                          onClick={() => copyNumber(r.membership_no)}
                          sx={{
                            font: 'inherit', border: 0, background: 'none', p: 0,
                            cursor: 'pointer', textAlign: 'left', display: 'block',
                            fontSize: 16, fontWeight: 800, letterSpacing: 1.5,
                            fontFamily: MONO, fontVariantNumeric: 'tabular-nums', color: INK,
                            '&:hover': { color: PRIMARY_DARK },
                          }}>{pretty(r.membership_no)}</Typography>
                      </Tooltip>
                      {r.full_name && (
                        <Typography noWrap sx={{
                          fontSize: 11.5, color: MUTED, mt: .15, maxWidth: 260,
                        }}>{r.full_name}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={r.eligible_now ? 'eligible' : r.status} />
                      <NhiaNote r={r} />
                    </TableCell>
                    {/*  THE DATE AND THE CLOCK TIME, then how long ago. */}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {s ? (
                        <Tooltip title={`Recorded ${s.full}`} placement="top-start">
                          <Box>
                            <Typography sx={{
                              fontSize: 12.5, fontWeight: 700, color: INK,
                              fontVariantNumeric: 'tabular-nums',
                            }}>
                              {s.dateText}
                              <Box component="span" sx={{
                                color: MUTED, fontWeight: 600, ml: .75,
                              }}>{s.timeText}</Box>
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: FAINT, mt: .1 }}>
                              {s.ago}
                              {r.attempts > 0
                                ? ` · ${r.attempts} attempt${r.attempts === 1 ? '' : 's'}`
                                : ''}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : <Typography sx={{ fontSize: 12.5, color: FAINT }}>—</Typography>}
                    </TableCell>
                    <TableCell sx={{
                      display: { xs: 'none', xl: 'table-cell' }, whiteSpace: 'nowrap',
                    }}>
                      <Typography noWrap sx={{ fontSize: 12, color: MUTED, maxWidth: 130 }}>
                        {r.recorded_by || '—'}
                      </Typography>
                      {r.assigned_to && (
                        <Typography noWrap sx={{ fontSize: 11, color: FAINT, maxWidth: 130 }}>
                          with {r.assigned_to}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', py: 1, pr: 2 }}>
                      {actions(r, false)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── PAGING ──────────────────────────────────────────────────────────
           Says which slice of the register is on screen. Without it a list
           capped at one page reads as the whole thing.  */}
      {total > 0 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          px: { xs: 1.5, md: 2 }, py: 1.1, borderTop: `1px solid ${LINE}`,
          background: '#fff',
        }}>
          <Typography sx={{ fontSize: 12, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
            <Box component="span" sx={{ fontWeight: 700, color: INK }}>
              {(page * pageSize + 1).toLocaleString()}–
              {Math.min((page + 1) * pageSize, total).toLocaleString()}
            </Box>{' '}of {total.toLocaleString()}
          </Typography>

          <Select size="small" value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            sx={{
              height: 28, fontSize: 12, borderRadius: 2, color: MUTED,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: LINE },
            }}>
            {[25, 50, 100, 200].map((n) => (
              <MenuItem key={n} value={n} sx={{ fontSize: 12.5 }}>{n} per page</MenuItem>
            ))}
          </Select>

          <Box sx={{ flex: 1 }} />

          <Tooltip title="Download everything this view matches">
            <span>
              <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                onClick={exportCsv} disabled={exporting}
                sx={{ textTransform: 'none', fontWeight: 600, color: MUTED, borderRadius: 2 }}>
                {exporting ? 'Preparing…' : 'Export'}
              </Button>
            </span>
          </Tooltip>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: .25 }}>
            <IconButton size="small" disabled={page === 0 || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              sx={{ border: `1px solid ${LINE}`, borderRadius: 2 }}>
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Typography sx={{
              fontSize: 12, color: MUTED, px: 1, fontVariantNumeric: 'tabular-nums',
            }}>
              {page + 1} / {pageCount}
            </Typography>
            <IconButton size="small" disabled={page + 1 >= pageCount || loading}
              onClick={() => setPage((p) => p + 1)}
              sx={{ border: `1px solid ${LINE}`, borderRadius: 2 }}>
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      )}
      </Paper>{/* ── end of the list card ───────────────────────────────── */}

      {(summary?.reasons || []).length > 0 && status === 'failed' && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{
            fontSize: 10.5, fontWeight: 800, color: MUTED,
            textTransform: 'uppercase', letterSpacing: .5, mb: .75,
          }}>Why renewals fail</Typography>
          <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap' }}>
            {summary.reasons.map((x, i) => (
              <Chip key={i} size="small" variant="outlined"
                label={`${x.reason} · ${Number(x.n).toLocaleString()}`}
                sx={{ fontSize: 11, borderColor: TONE.failed.bd, color: TONE.failed.fg }} />
            ))}
          </Box>
        </Box>
      )}

      </Box>{/* ── end of the right column ──────────────────────────────── */}
      </Box>{/* ── end of the two-column grid ───────────────────────────── */}

      <Dialog open={!!failing} onClose={() => setFailing(null)} fullWidth maxWidth="xs"
        fullScreen={isPhone}
        PaperProps={{ sx: { borderRadius: isPhone ? 0 : 3 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800, pb: 1 }}>
          Could not renew
          <Typography sx={{
            fontSize: 15, color: MUTED, fontWeight: 700, letterSpacing: 1,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', mt: .25,
          }}>{failing ? pretty(failing.membership_no) : ''}</Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: .875, pt: 1 }}>
          {REASONS.map((reason) => (
            <Button key={reason} variant="outlined" size="large"
              onClick={() => { mark(failing, 'failed', reason); setFailing(null); }}
              sx={{
                justifyContent: 'flex-start', textTransform: 'none', borderRadius: 2,
                borderColor: LINE, color: '#334155', fontWeight: 600, py: 1.25,
                '&:hover': { borderColor: TONE.failed.bd, background: TONE.failed.bg,
                  color: TONE.failed.fg },
              }}>{reason}</Button>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFailing(null)} sx={{ color: MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/*  ── THE SAME REASON, FOR A WHOLE BATCH ──────────────────────────────
           One shared reason and no free text, for the same purpose as the
           single-row list: a campaign stalling on one fixable cause has to stay
           countable. Writing off twenty numbers is not reversed as easily as it
           is done, so the count is stated before the buttons.  */}
      <Dialog open={bulkFail} onClose={() => setBulkFail(false)} fullWidth maxWidth="xs"
        fullScreen={isPhone}
        PaperProps={{ sx: { borderRadius: isPhone ? 0 : 3 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800, pb: 1 }}>
          Could not renew
          <Typography sx={{ fontSize: 13, color: MUTED, fontWeight: 600, mt: .25 }}>
            {pickedCount} number{pickedCount === 1 ? '' : 's'} · the same reason for all
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: .875, pt: 1 }}>
          {REASONS.map((reason) => (
            <Button key={reason} variant="outlined" size="large"
              onClick={() => bulkWriteOff(reason)}
              sx={{
                justifyContent: 'flex-start', textTransform: 'none', borderRadius: 2,
                borderColor: LINE, color: '#334155', fontWeight: 600, py: 1.25,
                '&:hover': { borderColor: TONE.failed.bd, background: TONE.failed.bg,
                  color: TONE.failed.fg },
              }}>{reason}</Button>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkFail(false)} sx={{ color: MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/*  ── WHAT NHIA SAID ABOUT THE ONE JUST DONE ──────────────────────
           Shown rather than buried in a toast, because the three endings need
           different things from the person reading. A CCC means finished; a
           future start date means come back on that day; anything else means
           the renewal did not take. */}
      <Dialog open={!!checkResult} onClose={() => setCheckResult(null)}
        fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: isPhone ? 0 : 3 } }}
        fullScreen={isPhone}>
        {checkResult && (() => {
          const o = checkResult.outcome;
          const r = checkResult.result || {};
          const good = o === 'active';
          const waiting = o === 'starts_later';
          const tone = good ? TONE.renewed : waiting ? TONE.pending : TONE.failed;
          const title = good ? 'CCC issued'
            : waiting ? 'Renewed — not live yet'
              : o === 'not_found' ? 'NHIA does not know this card'
                : o === 'no_ccc' ? 'No CCC issued'
                  : 'Could not reach NHIA';
          return (
            <>
              <Box sx={{ p: 2.5, background: tone.bg, borderBottom: `1px solid ${tone.bd}` }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: .6,
                  textTransform: 'uppercase', color: tone.fg }}>{title}</Typography>
                <Typography sx={{
                  fontSize: 24, fontWeight: 800, letterSpacing: 2, color: INK, mt: .25,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                }}>{pretty(checkResult.row.membership_no)}</Typography>
                {r.member_name && (
                  <Typography sx={{ fontSize: 13, color: MUTED }}>{r.member_name}</Typography>
                )}
              </Box>
              <DialogContent sx={{ pt: 2 }}>
                {good && r.ccc && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: MUTED,
                      textTransform: 'uppercase', letterSpacing: .5 }}>CCC</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: TONE.renewed.fg,
                      fontFamily: 'ui-monospace, monospace' }}>{r.ccc}</Typography>
                  </Box>
                )}
                {waiting && (
                  <Box sx={{
                    p: 1.5, borderRadius: 2, mb: 1.5,
                    background: TONE.pending.bg, border: `1px solid ${TONE.pending.bd}`,
                  }}>
                    <Typography sx={{ fontSize: 12.5, color: TONE.pending.fg, fontWeight: 700 }}>
                      Becomes active on {r.eligibility_start
                        ? String(r.eligibility_start).slice(0, 10) : 'a date NHIA did not give'}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: TONE.pending.fg, mt: .25 }}>
                      The renewal worked. It is counted as renewed and does not
                      need phoning again — it simply is not live yet.
                    </Typography>
                  </Box>
                )}
                {/*  ── THE FOLDER ────────────────────────────────────────
                     A working membership means the member can be seen, so the
                     register gives them a patient record — or finds the one
                     they already have. Shown prominently because it is the
                     number everything later is joined on. */}
                {r.patient_id && (
                  <Box sx={{
                    p: 1.5, borderRadius: 2, mb: 1.5,
                    background: `${PRIMARY}0F`, border: `1px solid ${PRIMARY}55`,
                  }}>
                    <Typography sx={{
                      fontSize: 10.5, fontWeight: 800, letterSpacing: .5,
                      textTransform: 'uppercase', color: PRIMARY,
                    }}>
                      {r.patient_created ? 'New folder created' : 'Existing folder'}
                    </Typography>
                    <Typography sx={{
                      fontSize: 19, fontWeight: 800, color: INK, mt: .25,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}>{r.patient_id}</Typography>
                    <Typography sx={{ fontSize: 11, color: MUTED, mt: .25 }}>
                      {r.patient_created
                        ? 'Registered from NHIA’s own details. Finish the record in Medical Records.'
                        : 'This number already belonged to a patient here.'}
                    </Typography>
                  </Box>
                )}
                {checkResult.activation?.note && (
                  <Typography sx={{ fontSize: 11.5, color: TONE.failed.fg, mb: 1 }}>
                    No folder: {checkResult.activation.note}
                  </Typography>
                )}

                {[
                  ['NHIA status', r.nhia_status],
                  ['Eligibility', r.eligibility_start || r.eligibility_end
                    ? `${String(r.eligibility_start || '?').slice(0, 10)} → ${String(r.eligibility_end || '?').slice(0, 10)}`
                    : null],
                  ['Transaction', r.transaction_id],
                  ['Provider', r.hp_name],
                  ['Message', r.message],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <Box key={k} sx={{ display: 'flex', gap: 1, py: .4,
                    borderBottom: `1px solid ${LINE}` }}>
                    <Typography sx={{ fontSize: 11.5, color: FAINT, minWidth: 96 }}>{k}</Typography>
                    <Typography sx={{ fontSize: 12, color: INK, fontWeight: 600 }}>{v}</Typography>
                  </Box>
                ))}
                <Typography sx={{ fontSize: 10.5, color: FAINT, mt: 1.5 }}>
                  The full response has been saved against this number.
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="contained" disableElevation onClick={() => setCheckResult(null)}
                  sx={{ background: PRIMARY, textTransform: 'none', fontWeight: 700,
                    borderRadius: 2 }}>Done</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.kind === 'ok' ? 'success'
          : toast?.kind === 'warn' ? 'warning' : 'error'}
          onClose={() => setToast(null)} variant="filled"
          sx={{ borderRadius: 2, fontWeight: 600 }}>
          {toast?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
